import { useState, useCallback, useEffect, useMemo } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { RootState } from '@/store/store'
import { useGetImagesQuery, ImageDocument } from '@/store/api'
import { incrementLibraryPage, resetLibraryPage } from '@/store/slices/filterSlice'
import FilterPanel from '@/components/library/FilterPanel'
import ImageGrid from '@/components/library/ImageGrid'

const PAGE_SIZE = 50

function LibraryPage() {
  const dispatch = useDispatch()
  const { filterPanelOpen } = useSelector((state: RootState) => state.ui)
  const { activeTags, classification, hasFaces, libraryPage } = useSelector(
    (state: RootState) => state.filter
  )
  const [allImages, setAllImages] = useState<ImageDocument[]>([])

  // Build filter params from Redux state
  const filterParams = useMemo(() => {
    const params: {
      tags?: string[]
      isPhoto?: boolean
      isScreenshot?: boolean
      hasFaces?: boolean
    } = {}

    // Add active tags
    if (activeTags.length > 0) {
      params.tags = activeTags
    }

    // Classification - only pass if NOT all are selected (meaning user is filtering)
    const allClassSelected =
      classification.photos &&
      classification.screenshots &&
      classification.documents &&
      classification.textHeavy

    if (!allClassSelected) {
      // Build OR condition: pass true for selected types
      // If photos selected but not screenshots, we need isPhoto=true
      // If both selected, we need both true (which means OR in backend)
      if (classification.photos && !classification.screenshots) {
        params.isPhoto = true
      } else if (!classification.photos && classification.screenshots) {
        params.isScreenshot = true
      } else if (classification.photos && classification.screenshots) {
        // Both selected - pass both as true
        params.isPhoto = true
        params.isScreenshot = true
      }
      // If neither is selected, no results (handled by backend)
    }

    // Face filter
    if (hasFaces === 'yes') {
      params.hasFaces = true
    } else if (hasFaces === 'no') {
      params.hasFaces = false
    }

    return params
  }, [activeTags, classification, hasFaces])

  // Reset page when filters change
  useEffect(() => {
    dispatch(resetLibraryPage())
    setAllImages([])
  }, [filterParams, dispatch])

  const { data, isLoading, error, isFetching } = useGetImagesQuery({
    page: libraryPage,
    pageSize: PAGE_SIZE,
    ...filterParams,
  })

  // Accumulate images as pages load
  useEffect(() => {
    if (data?.items) {
      if (libraryPage === 1) {
        setAllImages(data.items)
      } else {
        setAllImages(prev => {
          // Avoid duplicates by checking master_id
          const existingIds = new Set(prev.map(img => img.master_id))
          const newItems = data.items.filter(img => !existingIds.has(img.master_id))
          return [...prev, ...newItems]
        })
      }
    }
  }, [data, libraryPage])

  const handleLoadMore = useCallback(() => {
    if (!isFetching && data?.has_more) {
      dispatch(incrementLibraryPage())
    }
  }, [isFetching, data?.has_more, dispatch])

  // Load all remaining images by incrementing pages until has_more is false
  const [isLoadingAll, setIsLoadingAll] = useState(false)

  const handleLoadAll = useCallback(() => {
    if (!isFetching && data?.has_more) {
      setIsLoadingAll(true)
      dispatch(incrementLibraryPage())
    }
  }, [isFetching, data?.has_more, dispatch])

  // Continue loading if "Load All" was clicked and there's more to load
  useEffect(() => {
    if (isLoadingAll && !isFetching && data?.has_more) {
      dispatch(incrementLibraryPage())
    } else if (isLoadingAll && !data?.has_more) {
      setIsLoadingAll(false)
    }
  }, [isLoadingAll, isFetching, data?.has_more, dispatch])

  const total = data?.total ?? 0
  const hasMore = data?.has_more ?? false

  return (
    <div className="flex h-full gap-4">
      {/* Filter Panel */}
      {filterPanelOpen && (
        <div className="w-64 flex-shrink-0">
          <FilterPanel />
        </div>
      )}

      {/* Image Grid */}
      <div className="flex-1 min-w-0">
        {isLoading && libraryPage === 1 && (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <p className="text-destructive mb-2">Failed to load images</p>
              <p className="text-sm text-muted-foreground">
                Make sure the API server is running on port 8000
              </p>
            </div>
          </div>
        )}

        {allImages.length > 0 && (
          <>
            <ImageGrid
              images={allImages}
              total={total}
              onLoadMore={handleLoadMore}
              onLoadAll={handleLoadAll}
              hasMore={hasMore}
              isLoadingAll={isLoadingAll}
            />
            {isFetching && libraryPage > 1 && (
              <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
              </div>
            )}
          </>
        )}

        {!isLoading && !error && allImages.length === 0 && (
          <div className="flex items-center justify-center h-full">
            <div className="text-center text-muted-foreground">
              <p className="text-lg mb-2">No images found</p>
              <p className="text-sm">Process some images to see them here</p>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default LibraryPage
